import { useState, useEffect, useCallback, useRef } from 'react';
import { powerBiUsageApi } from '../services/powerBiUsageApi';
import type {
    PbiReportRow, PbiUsageStats, PbiUsageFilterOptions, PbiUsageFilters,
    PbiUsageWindow, PbiSortField, SortOrder,
} from '../types/powerBiUsage';

interface UsePowerBiUsageReturn {
    items: PbiReportRow[];
    total: number;
    loading: boolean;
    error: string | null;
    stats: PbiUsageStats | null;
    filterOptions: PbiUsageFilterOptions | null;
    filters: PbiUsageFilters;
    window: PbiUsageWindow;
    sortBy: PbiSortField;
    sortOrder: SortOrder;
    page: number;
    limit: number;
    setFilters: (f: PbiUsageFilters) => void;
    setWindow: (w: PbiUsageWindow) => void;
    setSort: (field: PbiSortField) => void;
    setPage: (p: number) => void;
    refresh: () => void;
}

const SESSION_KEY = 'powerbi-usage-state';
const DEFAULT_FILTERS: PbiUsageFilters = {};
const DEFAULT_WINDOW: PbiUsageWindow = '90';
// Least-used first — the page exists to find retirement candidates.
const DEFAULT_SORT: PbiSortField = 'views';
const DEFAULT_ORDER: SortOrder = 'asc';

const VALID_WINDOWS: string[] = ['30', '90', '180', 'all'];
const VALID_SORT: string[] = ['views', 'distinctUsers', 'lastViewedAt', 'daysSinceLastView', 'name', 'workspaceName'];
const VALID_REVIEW: string[] = ['Keep', 'Retire', 'Undecided'];
const VALID_USAGE: string[] = ['used', 'unused'];

interface Persisted {
    filters: PbiUsageFilters;
    window: PbiUsageWindow;
    sortBy: PbiSortField;
    sortOrder: SortOrder;
}

function sanitizeFilters(raw: unknown): PbiUsageFilters {
    if (!raw || typeof raw !== 'object') return DEFAULT_FILTERS;
    const f = raw as PbiUsageFilters;
    return {
        workspaceId: typeof f.workspaceId === 'string' && f.workspaceId ? f.workspaceId : undefined,
        search: typeof f.search === 'string' && f.search ? f.search : undefined,
        reviewStatus: VALID_REVIEW.includes(f.reviewStatus as string) ? f.reviewStatus : undefined,
        usage: VALID_USAGE.includes(f.usage as string) ? f.usage : undefined,
        includePersonal: f.includePersonal === true ? true : undefined,
        includeDeleted: f.includeDeleted === true ? true : undefined,
    };
}

function loadPersisted(): Persisted {
    const fallback: Persisted = {
        filters: DEFAULT_FILTERS, window: DEFAULT_WINDOW, sortBy: DEFAULT_SORT, sortOrder: DEFAULT_ORDER,
    };
    if (typeof globalThis.window === 'undefined') return fallback;
    try {
        const raw = globalThis.window.sessionStorage.getItem(SESSION_KEY);
        if (!raw) return fallback;
        const p = JSON.parse(raw) as Partial<Persisted>;
        return {
            filters: sanitizeFilters(p.filters),
            window: VALID_WINDOWS.includes(p.window as string) ? (p.window as PbiUsageWindow) : DEFAULT_WINDOW,
            sortBy: VALID_SORT.includes(p.sortBy as string) ? (p.sortBy as PbiSortField) : DEFAULT_SORT,
            sortOrder: p.sortOrder === 'desc' ? 'desc' : DEFAULT_ORDER,
        };
    } catch {
        return fallback;
    }
}

export function usePowerBiUsage(): UsePowerBiUsageReturn {
    const [items, setItems] = useState<PbiReportRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<PbiUsageStats | null>(null);
    const [filterOptions, setFilterOptions] = useState<PbiUsageFilterOptions | null>(null);

    const initial = loadPersisted();
    const [filters, setFiltersState] = useState<PbiUsageFilters>(initial.filters);
    const [window, setWindowState] = useState<PbiUsageWindow>(initial.window);
    const [sortBy, setSortBy] = useState<PbiSortField>(initial.sortBy);
    const [sortOrder, setSortOrder] = useState<SortOrder>(initial.sortOrder);
    const [page, setPage] = useState(1);
    const limit = 50;
    const refreshCounter = useRef(0);

    useEffect(() => {
        if (typeof globalThis.window === 'undefined') return;
        try {
            globalThis.window.sessionStorage.setItem(
                SESSION_KEY, JSON.stringify({ filters, window, sortBy, sortOrder })
            );
        } catch { /* ignore quota/private-mode */ }
    }, [filters, window, sortBy, sortOrder]);

    useEffect(() => {
        powerBiUsageApi.getFilters()
            .then(setFilterOptions)
            .catch(err => console.error('Failed to load filter options:', err));
    }, []);

    // List and stats settle independently (Promise.allSettled) — a failing sort column
    // must not blank the tiles; see useConsolidatedBusiness for the war story.
    const load = useCallback((isCurrent: () => boolean) => {
        setLoading(true);
        setError(null);

        const listP = powerBiUsageApi.list({ page, limit, sortBy, sortOrder, window, filters })
            .then(r => { if (isCurrent()) { setItems(r.items); setTotal(r.total); } });
        const statsP = powerBiUsageApi.getStats(filters, window)
            .then(r => { if (isCurrent()) setStats(r); });

        return Promise.allSettled([listP, statsP]).then(results => {
            if (!isCurrent()) return;
            const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
            if (failed.length) {
                const msg = failed[0].reason instanceof Error ? failed[0].reason.message : String(failed[0].reason);
                setError(failed.length === results.length ? msg : `${msg} (part of the page could not be loaded)`);
            }
            setLoading(false);
        });
    }, [page, limit, sortBy, sortOrder, window, filters]);

    useEffect(() => {
        let cancelled = false;
        const current = ++refreshCounter.current;
        void load(() => !cancelled && current === refreshCounter.current);
        return () => { cancelled = true; };
    }, [load]);

    const setFilters = useCallback((f: PbiUsageFilters) => {
        setFiltersState(f);
        setPage(1);
    }, []);

    const setWindow = useCallback((w: PbiUsageWindow) => {
        setWindowState(w);
        setPage(1);
    }, []);

    const setSort = useCallback((field: PbiSortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
            } else {
                // New sort column: usage columns start ascending (least first — that is the
                // hunt), name columns ascending too; recency starts descending (newest first).
                setSortOrder(field === 'lastViewedAt' ? 'desc' : 'asc');
            }
            return field;
        });
        setPage(1);
    }, []);

    const refresh = useCallback(() => {
        const current = ++refreshCounter.current;
        void load(() => current === refreshCounter.current);
    }, [load]);

    return {
        items, total, loading, error, stats, filterOptions,
        filters, window, sortBy, sortOrder, page, limit,
        setFilters, setWindow, setSort, setPage, refresh,
    };
}
